<template>
  <b-card
    :id="`card-${idTemplate}`"
    class="card-table module-link"
    no-body
    :data-title="module.label"
    @click="onCardClick"
  >
    <b-card-body class="pb-0">
      <b-card-title class="card__title">
        <font-awesome-icon :icon="['fas', module.icon || 'puzzle-piece']" />
        <span>{{ module.label }}</span>
      </b-card-title>
      <b-tooltip
        v-if="isTooltipVisible"
        :target="`${module.label}--description`"
        :title="module.description"
        triggers="hover"
      />
      <b-card-text
        v-if="module.description"
        :id="`${module.label}--description`"
        ref="description"
        class="description"
        data-toggle="tooltip"
        data-placement="bottom"
        :title="module.description"
      >
        {{ module.description }}
      </b-card-text>
    </b-card-body>
    <b-card-body class="action-wrapper">
      <div>
        <b-button
          v-if="canManagePermissions"
          :id="`${idTemplate}-permissions`"
          size="sm"
          variant="outline-secondary"
          :to="{
            name: 'dataManagementPermissions',
            params: {itemId: module.moduleGuid, type: permissionItemTypes.module}}
          "
        >
          <font-awesome-icon :icon="['fas', 'key']" />
          Permissions
        </b-button>

        <b-button
          v-if="canEdit && !module.isSystem"
          :id="`${idTemplate}-edit`"
          size="sm"
          variant="outline-primary"
          class="btn-module-edit"
          @click.stop="showEditModuleModal($event, module)"
        >
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
          Edit
        </b-button>

        <b-button
          v-if="canDelete"
          :id="`${idTemplate}-delete`"
          size="sm"
          variant="outline-danger btn-module-delete"
          @click.stop="deleteModuleFromList($event, module)"
        >
          <font-awesome-icon :icon="['fas', 'trash']" />
          Delete
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {UNSET_FOLDER_LIST} from '../../../../../store/modules/data-management/mutation-types';
import NoContent from '../../../../../core/components/NoContent';
import ContentSpinner from '../../../../../core/components/ContentSpinner';
import ModuleModal from '../modals/ModuleModal';

const {mapState, mapActions, mapMutations} = createNamespacedHelpers('dataManagement');
const {mapActions: sidebarMapActions} = createNamespacedHelpers('sidebar');

export default {
  name: 'ModuleCard',
  components: {
    NoContent,
    ContentSpinner,
    ModuleModal,
  },
  props: {
    module: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      groups: [],
      isTooltipVisible: false,
      idTemplate: null,
    };
  },
  computed: {
    ...mapState({
      permissionItemTypes: state => state.permissionItemTypes,
    }),
    ...mapState(['permissionTypes']),
    canDelete() {
      if (this.module.isSystem) return false;
      return [this.permissionTypes.admin].includes(this.module.currentUserPermissionLevel);
    },
    canEdit() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.edit,
      ].includes(this.module.currentUserPermissionLevel);
    },
    canManagePermissions() {
      return this.module.currentUserPermissionLevel === this.permissionTypes.admin;
    },
  },
  methods: {
    ...mapActions([
      'getFolders',
      'getModules',
      'showFolderModal',
      'showModuleModal',
      'addTable',
      'deleteFolder',
      'deleteModule',
      'showFolderModal',
    ]),
    ...sidebarMapActions([
      'setSelectedModuleAction',
      'getSidebarStructure',
    ]),
    ...mapMutations([UNSET_FOLDER_LIST]),
    onCardClick() {
      const path =  this.module.isSystem
        ? (this.module.path || '')
        : {name: 'dataManagementModuleView', params: {moduleUuid: this.module.moduleGuid}};

      this.setSelectedModuleAction(this.module.moduleGuid);
      this.$router.push(path);
    },
    async deleteModuleFromList(e, module) {
      e.preventDefault();
      if (module.folders && module.folders.length > 0) {
        return this.$toasted.error(
          `Module has ${module.folders.length} folder(s) and can not be deleted`, {
            duration: TOASTER_DURATION,
          });
      }

      const confirm = await this.$confirm('Deleting Module...', `You are deleting module "${module.label}". Are you sure you want to continue?`);
      if (confirm) {
        const {success} = await this.deleteModule(module);
        if (success) {
          this.$toasted[success ? 'success' : 'error'](`Module "${module.label}" was deleted`, {duration: TOASTER_DURATION});
          await this.getSidebarStructure();
        } else {
          this.$toasted.error('Module can not be deleted', {duration: TOASTER_DURATION});
        }
      }
    },
    showEditModuleModal(e, module) {
      e.preventDefault();
      this.showModuleModal(module);
    },
  },
  mounted() {
    this.idTemplate = this.$options.filters.displayName(this.module.label);
    const $item = this.$refs.description;
    if (!$item) {
      this.isTooltipVisible = false;
    } else {
      this.isTooltipVisible = $item.scrollHeight > $item.offsetHeight;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../core/scss/variables";

.card-table {
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 0;
  min-height: 10rem;

  .description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }

  &:hover {
    box-shadow: 0 0 0 2px $primary;
  }

  .actions-button-wrapper {
    text-align: center;
  }
}

.action-wrapper {
  padding-top: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  & > div {
    display: inherit;
    gap: 5px;
  }

  & .btn:focus {
    box-shadow: none;
  }
}

.module-link {
  text-decoration: none;
  color: unset;
}

.card__title {
  display: flex;
  align-items: flex-start;
  gap: 5px;

  span {
    white-space: pre-wrap;
    overflow: hidden;
  }
}
</style>
